/* =========================================================
   Glazing Hardware — storefront behaviour.

   Ported from src/lib/hooks.js and src/components/MegaMenu.jsx. No framework,
   no build step: everything here is what React was doing, written plainly.
   ========================================================= */

(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------
     Scroll reveal
     Adds .is-in once the element has entered the viewport and never takes it
     off again — sections should not flicker on the way back up.
     --------------------------------------------------------- */
  function initReveal() {
    var items = document.querySelectorAll('.reveal');
    if (!items.length) return;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.04 }
    );

    items.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------
     Mega menu
     Hover intent with a short grace period, so the pointer can travel from a
     nav item down into the panel without the panel vanishing under it. The
     panels are siblings of the header, so they are positioned to its bottom
     edge and re-measured on scroll and resize.
     --------------------------------------------------------- */
  function initMega() {
    var header = document.querySelector('[data-header]');
    var triggers = document.querySelectorAll('[data-mega]');
    var panels = document.querySelectorAll('[data-mega-panel]');
    if (!header || !triggers.length) return;

    var open = null;
    var closeTimer = null;

    function panelFor(id) {
      return document.querySelector('[data-mega-panel="' + id + '"]');
    }

    function place(panel) {
      panel.style.top = header.getBoundingClientRect().bottom + 'px';
    }

    function show(id) {
      clearTimeout(closeTimer);
      if (open === id) return;
      hideNow();
      var panel = panelFor(id);
      if (!panel) return;
      open = id;
      panel.hidden = false;
      place(panel);
      document.body.classList.add('mega-open');
      var trigger = document.querySelector('[data-mega="' + id + '"]');
      if (trigger) {
        trigger.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    function hideNow() {
      panels.forEach(function (p) {
        p.hidden = true;
      });
      triggers.forEach(function (t) {
        t.classList.remove('is-open');
        t.setAttribute('aria-expanded', 'false');
      });
      document.body.classList.remove('mega-open');
      open = null;
    }

    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(hideNow, 180);
    }

    triggers.forEach(function (trigger) {
      var id = trigger.getAttribute('data-mega');
      trigger.addEventListener('mouseenter', function () {
        show(id);
      });
      trigger.addEventListener('mouseleave', scheduleClose);
      trigger.addEventListener('focus', function () {
        show(id);
      });
    });

    panels.forEach(function (panel) {
      panel.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
      });
      panel.addEventListener('mouseleave', scheduleClose);
    });

    // The menu should never survive an Escape press.
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && open) hideNow();
    });

    var remeasure = function () {
      if (open) place(panelFor(open));
    };
    window.addEventListener('scroll', remeasure, { passive: true });
    window.addEventListener('resize', remeasure);
  }

  /* ---------------------------------------------------------
     Magnetic CTAs
     The one micro-interaction the brief asked for. Pointer only: switched off
     for coarse pointers and for anyone who has asked for reduced motion.
     --------------------------------------------------------- */
  function initMagnetic() {
    if (reduced) return;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;

    window.addEventListener('pointermove', function (e) {
      var target = e.target.closest ? e.target.closest('[data-magnetic]') : null;
      document.querySelectorAll('[data-magnetic]').forEach(function (btn) {
        if (btn !== target) btn.style.translate = '';
      });
      if (!target) return;
      var r = target.getBoundingClientRect();
      var dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      var dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      target.style.translate = (dx * 9).toFixed(2) + 'px ' + (dy * 6).toFixed(2) + 'px';
    });
  }

  /* ---------------------------------------------------------
     Product gallery
     Clicking a thumbnail swaps the hero shot. Choosing a finish shows that
     finish when a photo of it exists — the variant picker writes the media id
     it wants and this finds the matching tile.
     --------------------------------------------------------- */
  function initGallery() {
    var hero = document.querySelector('[data-pdp-hero-img]');
    var thumbs = document.querySelectorAll('[data-pdp-thumb]');
    if (!hero || !thumbs.length) return;

    function selectByMedia(mediaId) {
      var match = document.querySelector('[data-pdp-thumb][data-media-id="' + mediaId + '"]');
      if (match) match.click();
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-full');
        if (!src) return;
        hero.src = src;
        hero.alt = thumb.getAttribute('data-alt') || hero.alt;
        thumbs.forEach(function (t) {
          t.classList.remove('is-on');
          t.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('is-on');
        thumb.setAttribute('aria-pressed', 'true');
      });
    });

    document.addEventListener('variant:change', function (e) {
      if (e.detail && e.detail.mediaId) selectByMedia(e.detail.mediaId);
    });
  }

  /* ---------------------------------------------------------
     Variant picker
     Shopify needs the variant id on the form. The selects carry option values;
     this maps the chosen combination back to a variant, updates the hidden
     input, the configured-code line and the URL, and announces the change so
     the gallery can follow it.
     --------------------------------------------------------- */
  function initVariants() {
    var form = document.querySelector('[data-variant-form]');
    if (!form) return;

    var data = document.querySelector('[data-variant-json]');
    if (!data) return;

    var variants;
    try {
      variants = JSON.parse(data.textContent);
    } catch (err) {
      return;
    }

    var idInput = form.querySelector('[name="id"]');
    var codeOut = document.querySelector('[data-configured-code]');
    var submit = form.querySelector('[data-add-button]');
    var selects = form.querySelectorAll('[data-option-index]');

    function apply() {
      var chosen = [];
      selects.forEach(function (sel) {
        chosen[parseInt(sel.getAttribute('data-option-index'), 10)] = sel.value;
      });

      var match = variants.filter(function (v) {
        return v.options.every(function (opt, i) {
          return opt === chosen[i];
        });
      })[0];

      if (!match) {
        if (submit) {
          submit.disabled = true;
          submit.textContent = 'Unavailable';
        }
        return;
      }

      if (idInput) idInput.value = match.id;
      if (codeOut) codeOut.textContent = match.sku || match.title;
      if (submit) {
        submit.disabled = false;
        submit.textContent = submit.getAttribute('data-label') || 'Add to quote';
      }

      var url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);

      document.dispatchEvent(
        new CustomEvent('variant:change', { detail: { mediaId: match.mediaId } })
      );
    }

    selects.forEach(function (sel) {
      sel.addEventListener('change', apply);
    });
    apply();
  }

  /* ---------------------------------------------------------
     Quantity steppers
     --------------------------------------------------------- */
  function initSteppers() {
    document.querySelectorAll('[data-stepper]').forEach(function (stepper) {
      var input = stepper.querySelector('input');
      if (!input) return;
      stepper.querySelectorAll('button').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var step = btn.getAttribute('data-step') === 'down' ? -1 : 1;
          var next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  function init() {
    initReveal();
    initMega();
    initMagnetic();
    initGallery();
    initVariants();
    initSteppers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
